import { GridDataResult } from "@progress/kendo-angular-grid";
import { Import } from "./models/imports.model";
import { api_routes, db_tables, features, routes } from "@app/consts";
import { OverlayService } from "@app/shared/components/overlay/overlay.service";
import { TranslateService } from "@ngx-translate/core";
import { SnackbarService } from "@app/core/services/snackbar.service";
import { Router } from "@angular/router";
import { ImportsService } from "./services/imports.service";
import { Component, OnInit } from '@angular/core';
import { defer, forkJoin } from "rxjs";
import { GridState } from "@app/shared/services/grid-state/grid-state.model";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { ConfirmDialogComponent } from "@app/shared/components/confirm-dialog/confirm-dialog.component";
import { FeatureService } from '@app/core/services/feature.service';
import { SecurityProtectedBase } from "@app/shared/components/security-protected/security-protected";
import { RowActionButtonInput } from "../talent-track/talent-track-edit-employee/edit-employee/components/employment-records/components/employment-record-details/components/data-grid-comp/models/data-grid-comp.model";

@Component({
  selector: 'app-imports',
  templateUrl: './imports.component.html',
  styleUrls: ['./imports.component.scss']
})
export class ImportsComponent extends SecurityProtectedBase implements OnInit {
  public columns: any[] = [
    { field: "name", title: "Name", localizationCode: "ImportName" },
    { field: "description", title: "Description", localizationCode: "ImportDescription", type: "longText" },
    { field: "table", subField: "name", title: "Table", localizationCode: "ImportTableName" },
    { field: "status", subField: "name", title: "Status", localizationCode: "ImportStatus", type: 'chip', chipValue: ['Completed', ['Validation Failed', 'Failed', 'Completed With Errors'], ['Pending', 'Queued'], 'Running'] },
    { field: "version", subField: "createdOn", title: "Created On", localizationCode: "ImportCreatedOn", type: "date" },
  ];

  filterCategories: any[] = [
    { field: "name", title: "Name", localizationCode: "ImportName", type: "String", dataType: 'String' },
    { field: "table.name", title: "Table", localizationCode: "ImportTableName", type: "String", dataType: 'String' },
    { field: "status.name", title: "Status", localizationCode: "ImportStatus", type: "String", dataType: 'String' },
  ];

  sortableColumns: any[] = [
    { field: "name", sortValue: "name" },
    { field: "description", sortValue: "description" },
    { field: "table", sortValue: "table.name" },
    { field: "status", sortValue: "status.name" },
    { field: "version", sortValue: "version.createdOn" }
  ];

  public gridDataResult: GridDataResult;
  public api_routes = api_routes;
  public db_tables = db_tables;
  isLoading: boolean = true;
  pageSize: number = 20;
  skip: number = 0;
  clearSelectedItems: boolean = false;
  dialogRef: any;
  formValid: any;
  sortString: string = 'version.createdOn-desc';
  isLoadingPermissions: boolean = true;
  filterString: string;
  gridId: string = 'importsGrid';
  getRequest: any;
  searchValue: string;
  searchFilterString: string;
  gridActionButtons: RowActionButtonInput[] = [
    {
      id: 'copy',
      name: this.translate.instant('Copy'),
      icon: 'content_copy'
    }
  ];
  
  constructor(
    private overlayService: OverlayService,
    public translate: TranslateService,
    private snackbarService: SnackbarService,
    private importsService: ImportsService,
    private router: Router,
    private dialog: MatDialog,
    featureService: FeatureService
  ) {
    super(featureService, features.IMPORTS)
  }

  ngOnInit(): void {
    if (this.hasFeatureAccess) {
      this.getImports();
    }
  }

  getImports() {
    this.isLoading = true;

    // Combine filterString and searchFilterString if both exist
    let combinedFilter = '';
    if (this.filterString && this.searchFilterString) {
      combinedFilter = `(${this.filterString}) AND (${this.searchFilterString})`;
    } else if (this.filterString) {
      combinedFilter = this.filterString;
    } else if (this.searchFilterString) {
      combinedFilter = this.searchFilterString;
    } else {
      combinedFilter = '';
    }
    // Use combinedFilter in the service call

    this.getRequest = this.importsService.getImports(this.pageSize, this.skip, this.sortString, combinedFilter)
      .subscribe(
        {
          next: (v) => {
            this.gridDataResult = {
              data: v.data,
              total: v.total
            };
            this.isLoading = false;
          },
          error: (e) => { this.overlayService.hide(); this.isLoading = false; },
          complete: () => this.overlayService.hide()
        }
      );
  }

  editOrCreateImport(importObject?: Import) {
    if (importObject?.id) {
      this.router.navigate([`${routes.IMPORTS}/${routes.EDITOR}/${importObject?.id}/0`]);
    }
    else {
      this.router.navigate([`${routes.IMPORTS}/${routes.EDITOR}`]);
    }

  }

  openConfirmDeleteDialog(importIds: string[]) {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      text: this.translate.instant('AreYouSureYouWantToDeleteThis')
    };

    const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(
      data => {
        if (data === true) {
          this.deleteImports(importIds);
        }
      }
    );
  }

  deleteImports(importIds: string[]) {
    const observables = importIds.map(selectedItem => defer(() => this.importsService.deleteImport(selectedItem)));

    this.overlayService.show();

    forkJoin(observables)
      .subscribe(
        {
          next: (v) => {
            this.clearSelectedItems = !this.clearSelectedItems;
            this.snackbarService.openSnackBar(`${this.translate.instant('DeletedSuccessfully')}`, 'clear', 'success');
            this.getImports();
          },
          error: (e) => this.overlayService.hide(),
          complete: () => this.overlayService.hide()
        }
      );
  }

  gridStateChange(gridState: GridState) {
    if (this.pageSize !== gridState.pageSize) {
      this.pageSize = gridState.pageSize;
    }
    if (this.skip !== gridState.skip) {
      this.skip = gridState.skip;
    }
    if (this.sortString !== gridState.sortString) {
      this.sortString = gridState.sortString;
    }
    if (this.filterString !== gridState.filter) {
      this.filterString = gridState.filter;
      this.skip = 0;
    }

    if (this.searchValue !== gridState.searchValue) {
      let variants = [];
      this.searchFilterString = '';
      this.searchValue = gridState.searchValue;

      if (this.searchValue !== null) {
        variants = this.searchValue.split(' ').filter(value => value);
      }

      variants.map(variant => {
        if (variant) {
          if (this.searchFilterString.length > 2) {
            this.searchFilterString += ' AND ';
          }
          this.searchFilterString += `(Name like "${variant}")`;
        }
      });

      this.skip = 0;
    }

    this.getRequest.unsubscribe();
    this.getImports();
  }

  copy(event) {
    let importObject = event.rowData;
    this.overlayService.show('Copying import...');

    this.importsService.copyImport(importObject.id)
    .subscribe(
      {
        next: (v) => {
          this.snackbarService.openSnackBar(`${this.translate.instant('CopiedSuccessfully')}`, 'clear', 'success');
          this.router.navigate([`${routes.IMPORTS}/${routes.EDITOR}/${v.id}/0`]);
        },
        error: (e) => this.overlayService.hide(),
        complete: () => this.overlayService.hide()
      }
    );
  }
}
